/**
 * Photo Upload Module
 *
 * Manages the Uploadcare File Uploader v1 integration on product pages.
 *
 * Based on the latest Uploadcare File Uploader docs:
 *   - Events: https://uploadcare.com/docs/file-uploader/events/
 *   - API:    https://uploadcare.com/docs/file-uploader/api/
 *
 * Key concepts from the Uploadcare API:
 *   - OutputFileEntry: { internalId, uuid, cdnUrl, name, status, ... }
 *   - internalId: unique within the widget instance (used for removeFileByInternalId)
 *   - uuid: the Uploadcare file UUID (used in CDN URLs)
 *   - cdnUrl: full CDN URL like https://ucarecdn.com/{uuid}/
 *
 * Responsibilities:
 * - Listen to Uploadcare events via <uc-upload-ctx-provider>
 * - Maintain an array of uploaded file objects
 * - Render thumbnail previews with remove buttons
 * - Update the photo counter ("4 of 12")
 * - Support dynamic max from variant selection (match_variant mode)
 * - Support exact upload enforcement (save-the-date) and flexible mode (fridge/photo magnets)
 * - Show distribution preview ("Each photo prints 3 times") for flexible mode
 * - Enhance quantity dropdowns with per-unit pricing (supports both pricing models)
 * - Show total price (unit x pack) as the main price (per-unit model only)
 * - Apply "Most Popular" / "Best Value" badges to variant pills
 * - AddToCartGuard: block submit button when exact-mode validation fails
 * - Expose window.UploadedPhotos for product-form.js to read on submit
 */
(function () {
  'use strict';

  // =========================================================================
  // Product Configuration
  // Centralized per-handle config for JS-only concerns (badges, etc.)
  // Structural config (upload mode, max mode, pricing) comes from data attributes.
  // =========================================================================

  var PRODUCT_CONFIG = {
    'save-the-date': {
      // One badge per product — quantity dropdown only
      dropdownBadges: { '150': 'Most Popular' }
    },
    'photo-magnets': {
      // Medium is the most popular size; 24 is the most popular pack size
      badges: { 'Medium': 'Most Popular' },
      dropdownBadges: { '24': 'Most Popular' }
    },
    'fridge-magnets': {
      // Pack Size rendered as dropdown — per-unit pricing shown in label text.
      // 12 is the most popular quantity
      dropdownBadges: { '12': 'Most Popular' }
    }
    // calendar-magnets: single SKU, no variants, no badges
  };

  // --- State ---
  // Array of { internalId, uuid, cdnUrl, filename }
  var uploadedPhotos = [];
  window.UploadedPhotos = uploadedPhotos;

  // --- DOM References ---
  var container = document.querySelector('[data-photo-upload]');
  if (!container) return;

  var maxPhotos = parseInt(container.dataset.maxPhotos, 10) || 12;
  var maxMode = container.dataset.maxMode || 'fixed';
  var showDistribution = container.dataset.showDistribution !== 'false';
  var uploadMode = container.dataset.uploadMode || 'flexible'; // 'exact' or 'flexible'
  var qtyOptionIndex = parseInt(container.dataset.quantityOptionIndex, 10) || 1; // 1 or 2
  var priceModel = container.dataset.priceModel || 'per-unit'; // 'total' or 'per-unit'

  var previewGrid = container.querySelector('[data-photo-preview-grid]');
  var previewHintEl = container.querySelector('[data-photo-preview-hint]');
  var counterEl = container.querySelector('[data-photo-counter]');
  var distributionEl = container.querySelector('[data-photo-distribution]');
  var instructionEl = container.querySelector('[data-photo-instruction]');
  var errorEl = container.querySelector('[data-photo-error]');

  // --- Product data (used by dropdown enhancement + price display) ---
  var productData = null;
  var priceEl = null;
  var productHandle = '';

  // --- Uploadcare Context Provider ---
  var ctxProvider = document.getElementById('uploadcare-ctx');

  /**
   * Derive the product handle from the closest product-info element.
   */
  function detectProductHandle() {
    var productInfo = container.closest('product-info');
    if (!productInfo) return '';
    var url = productInfo.dataset.url || '';
    return url.split('/products/').pop().split('?')[0] || '';
  }

  function init() {
    productHandle = detectProductHandle();

    if (!ctxProvider) {
      console.warn('PhotoUpload: <uc-upload-ctx-provider> not found');
      return;
    }

    // Wait for the custom element to be defined before accessing its API
    customElements.whenDefined('uc-upload-ctx-provider').then(function () {
      // Listen for successful uploads (OutputFileEntry in 'success' state)
      ctxProvider.addEventListener('file-upload-success', handleUploadSuccess);

      // Listen for file removal from the Uploadcare widget (OutputFileEntry in 'removed' state)
      ctxProvider.addEventListener('file-removed', handleFileRemoved);

      // Listen for all files uploaded (batch complete)
      ctxProvider.addEventListener('common-upload-success', handleBatchComplete);
    });

    // When the user edits a photo (brightness/filters/crop) and clicks Apply in
    // Uploadcare Cloud Image Editor, the uploader updates the entry's cdnUrl
    // (with modifiers). We must sync that edited URL into our state + thumbnails.
    document.addEventListener('apply', handleEditorApply, true);

    // Initialize variant-driven features.
    // - match_variant products: maxPhotos + dropdown enrichment + price display + variantChange hooks
    // - save-the-date: dropdown enrichment + variantChange hooks only (maxPhotos stays fixed at 1)
    var needsVariantDropdownEnhancements = (maxMode === 'match_variant') || (productHandle === 'save-the-date');
    if (needsVariantDropdownEnhancements) {
      loadProductData();
      enhanceQuantityDropdown();
      if (priceModel === 'per-unit') {
        initPriceDisplay();
      }
      if (maxMode === 'match_variant') {
        readCurrentPackSize();
      }
      subscribeToVariantChanges();
    }

    // Apply badges to variant pills
    applyBadges();

    // Initialize distribution display
    updateDistribution();

    // Initialize add-to-cart guard state
    updateSubmitButton();
  }

  /**
   * Handle a single file upload success event.
   * e.detail is an OutputFileEntry with: internalId, uuid, cdnUrl, name, etc.
   * @param {CustomEvent} e - Uploadcare file-upload-success event
   */
  function handleUploadSuccess(e) {
    var entry = e.detail;
    if (!entry || !entry.cdnUrl) return;

    // Avoid duplicates (use internalId as the unique key per widget session)
    var exists = uploadedPhotos.some(function (p) {
      return p.internalId === entry.internalId;
    });
    if (exists) return;

    // Enforce max limit
    if (uploadedPhotos.length >= maxPhotos) return;

    var photo = {
      internalId: entry.internalId,
      uuid: entry.uuid,
      cdnUrl: entry.cdnUrl,
      filename: entry.name || entry.uuid || 'photo',
    };

    uploadedPhotos.push(photo);
    window.UploadedPhotos = uploadedPhotos;

    addThumbnail(photo);
    updateCounter();
    updateDistribution();
    updateSubmitButton();
  }

  /**
   * Handle file removal from the Uploadcare widget.
   * e.detail is an OutputFileEntry in 'removed' state.
   * @param {CustomEvent} e - Uploadcare file-removed event
   */
  function handleFileRemoved(e) {
    var entry = e.detail;
    if (!entry) return;

    removePhotoByInternalId(entry.internalId);
  }

  /**
   * Handle batch upload complete.
   */
  function handleBatchComplete() {
    updateCounter();
    updateDistribution();
    updateSubmitButton();
  }

  /**
   * Cloud Image Editor "Apply" event handler (capture phase).
   * We delay syncing by a tick to give the uploader time to update its state.
   */
  function handleEditorApply(e) {
    var target = e && e.target;
    if (!target || target.tagName !== 'UC-CLOUD-IMAGE-EDITOR') return;
    setTimeout(syncEditedUrlsFromUploader, 0);
  }

  /**
   * Sync edited cdnUrl values from Uploadcare widget state back into uploadedPhotos
   * and update thumbnail <img> src values accordingly.
   */
  function syncEditedUrlsFromUploader() {
    if (!ctxProvider) return;
    var api;
    try {
      api = ctxProvider.getAPI();
    } catch (e) {
      return;
    }
    if (!api || typeof api.getOutputCollectionState !== 'function') return;

    var state;
    try {
      state = api.getOutputCollectionState();
    } catch (e2) {
      return;
    }
    if (!state || !state.allEntries || !state.allEntries.length) return;

    var byInternalId = {};
    state.allEntries.forEach(function (entry) {
      if (!entry || !entry.internalId || !entry.cdnUrl) return;
      byInternalId[entry.internalId] = entry;
    });

    var changed = false;
    uploadedPhotos.forEach(function (photo) {
      var entry = byInternalId[photo.internalId];
      if (!entry) return;

      if (entry.cdnUrl && entry.cdnUrl !== photo.cdnUrl) {
        photo.cdnUrl = entry.cdnUrl;
        changed = true;
      }
      if (entry.uuid && entry.uuid !== photo.uuid) {
        photo.uuid = entry.uuid;
        changed = true;
      }
      if ((entry.name || '') && entry.name !== photo.filename) {
        photo.filename = entry.name;
        changed = true;
      }
    });

    if (!changed) return;

    window.UploadedPhotos = uploadedPhotos;

    // Refresh thumbnail src values so the edited version is visible immediately.
    if (previewGrid) {
      uploadedPhotos.forEach(function (photo) {
        var thumbEl = previewGrid.querySelector('[data-photo-internal-id="' + photo.internalId + '"]');
        if (!thumbEl) return;
        var img = thumbEl.querySelector('img');
        if (!img) return;
        img.src = photo.cdnUrl + '-/scale_crop/240x240/center/';
        img.alt = photo.filename;
      });
    }
  }

  /**
   * Remove a photo by its Uploadcare internalId from state and DOM.
   * @param {string} internalId - The Uploadcare widget internal identifier
   */
  function removePhotoByInternalId(internalId) {
    uploadedPhotos = uploadedPhotos.filter(function (p) {
      return p.internalId !== internalId;
    });
    window.UploadedPhotos = uploadedPhotos;

    // Remove thumbnail from DOM
    var thumbEl = previewGrid.querySelector('[data-photo-internal-id="' + internalId + '"]');
    if (thumbEl) {
      thumbEl.remove();
    }

    updateCounter();
    updateDistribution();
    updateSubmitButton();
  }

  /**
   * Add a thumbnail element to the preview grid.
   * @param {{ internalId: string, uuid: string, cdnUrl: string, filename: string }} photo
   */
  function addThumbnail(photo) {
    if (!previewGrid) return;

    var thumb = document.createElement('div');
    thumb.className = 'photo-upload__thumb photo-upload__thumb--loading';
    thumb.dataset.photoInternalId = photo.internalId;

    // Thumbnail image — use Uploadcare CDN transform for 240x240 crop
    var img = document.createElement('img');
    var thumbUrl = photo.cdnUrl + '-/scale_crop/240x240/center/';
    img.src = thumbUrl;
    img.alt = photo.filename;
    img.loading = 'lazy';
    img.onload = function () {
      thumb.classList.remove('photo-upload__thumb--loading');
    };
    img.onerror = function () {
      // Fallback: try the raw CDN URL
      img.src = photo.cdnUrl;
      thumb.classList.remove('photo-upload__thumb--loading');
    };

    // Remove button
    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'photo-upload__remove';
    removeBtn.setAttribute('aria-label', 'Remove photo');
    removeBtn.innerHTML = '&times;';
    removeBtn.addEventListener('click', function () {
      // Remove from our state and DOM
      removePhotoByInternalId(photo.internalId);

      // Also remove from the Uploadcare widget internal state.
      // removeFileByInternalId() takes the widget's internalId, NOT the uuid.
      // See: https://uploadcare.com/docs/file-uploader/api/#remove-file-by-internal-id
      try {
        var api = ctxProvider.getAPI();
        if (api && typeof api.removeFileByInternalId === 'function') {
          api.removeFileByInternalId(photo.internalId);
        }
      } catch (e) {
        // Silently fail — our state is still consistent
      }
    });

    thumb.appendChild(img);
    thumb.appendChild(removeBtn);
    previewGrid.appendChild(thumb);
  }

  /**
   * Update the counter text showing "N of M" photos.
   */
  function updateCounter() {
    var count = uploadedPhotos.length;

    // Show/hide the "Each photo becomes a magnet" hint above thumbnails
    if (previewHintEl) {
      if (count > 0) {
        previewHintEl.removeAttribute('hidden');
      } else {
        previewHintEl.setAttribute('hidden', '');
      }
    }

    if (!counterEl) return;

    counterEl.textContent = count + ' of ' + maxPhotos;

    if (uploadMode === 'exact') {
      // Exact mode: complete when count === max
      if (count === maxPhotos) {
        counterEl.classList.add('photo-upload__counter--complete');
      } else {
        counterEl.classList.remove('photo-upload__counter--complete');
      }
    } else {
      // Flexible mode: complete when count >= max
      if (count >= maxPhotos) {
        counterEl.classList.add('photo-upload__counter--complete');
      } else {
        counterEl.classList.remove('photo-upload__counter--complete');
      }
    }
  }

  // =========================================================================
  // Dynamic max from variant selection
  // =========================================================================

  /**
   * Update maxPhotos and reflect in UI + Uploadcare config.
   * @param {number} newMax - New maximum photo count
   */
  function updateMaxPhotos(newMax) {
    if (newMax === maxPhotos || newMax <= 0) return;
    maxPhotos = newMax;

    // Update Uploadcare multiple-max config
    var ucConfig = container.querySelector('uc-config');
    if (ucConfig) {
      ucConfig.setAttribute('multiple-max', String(maxPhotos));
    }

    // Warn if current uploads exceed new max (don't auto-remove)
    if (uploadedPhotos.length > maxPhotos) {
      console.warn(
        'PhotoUpload: You have ' + uploadedPhotos.length +
        ' photos but the new pack size is ' + maxPhotos +
        '. Please remove ' + (uploadedPhotos.length - maxPhotos) + ' photo(s).'
      );
    }

    // Update dynamic instruction and error text
    updateInstructionQty();

    updateCounter();
    updateDistribution();
    updateSubmitButton();
  }

  /**
   * Update the quantity numbers in instruction and error text elements.
   */
  function updateInstructionQty() {
    var qtySpans = container.querySelectorAll('[data-instruction-qty]');
    qtySpans.forEach(function (span) {
      span.textContent = maxPhotos;
    });
    var errorQtySpans = container.querySelectorAll('[data-error-qty]');
    errorQtySpans.forEach(function (span) {
      span.textContent = maxPhotos;
    });
  }

  /**
   * Read the current quantity from the correct variant option and update maxPhotos.
   * Uses qtyOptionIndex to determine which option to read from (1 or 2).
   * Called on initial load and after each variant change.
   */
  function readCurrentPackSize() {
    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    var variantSelectsEl = productInfo.querySelector('variant-selects');
    if (!variantSelectsEl) return;

    // Find all selects and fieldsets (radio groups) in order
    var selects = variantSelectsEl.querySelectorAll('select');
    var fieldsets = variantSelectsEl.querySelectorAll('fieldset');

    // Try reading from the select at the correct index (0-based)
    var targetIndex = qtyOptionIndex - 1;
    if (selects.length > targetIndex) {
      var newMax = parseInt(selects[targetIndex].value, 10);
      if (!isNaN(newMax) && newMax > 0) {
        updateMaxPhotos(newMax);
        return;
      }
    }

    // Fallback: try the dropdown-based option at the correct index
    // For mixed pickers (pills + dropdown), selects might only have the dropdown
    // Count all option groups (fieldsets + dropdowns) in DOM order
    var allOptionGroups = variantSelectsEl.querySelectorAll(
      '.product-form__input--pill, .product-form__input--swatch, .product-form__input--dropdown'
    );
    if (allOptionGroups.length > targetIndex) {
      var targetGroup = allOptionGroups[targetIndex];
      var select = targetGroup.querySelector('select');
      if (select) {
        var newMax = parseInt(select.value, 10);
        if (!isNaN(newMax) && newMax > 0) {
          updateMaxPhotos(newMax);
          return;
        }
      }
      var radio = targetGroup.querySelector('input[type="radio"]:checked');
      if (radio) {
        var newMax = parseInt(radio.value, 10);
        if (!isNaN(newMax) && newMax > 0) {
          updateMaxPhotos(newMax);
        }
      }
    }
  }

  function formatMoney(amountStr) {
    var currency = window.shopCurrency || {};
    var sym = currency.symbol || '$';
    var code = currency.isoCode || 'USD';
    return sym + amountStr + ' ' + code;
  }

  /**
   * Subscribe to Shopify's variantChange pub/sub event.
   * This fires AFTER the Section Rendering API replaces the price and
   * variant-selects HTML, so we re-apply our enhancements here.
   */
  function subscribeToVariantChanges() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      console.warn('PhotoUpload: Shopify pub/sub not available');
      return;
    }

    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      // Re-enhance the dropdown (HTML was replaced by Section Rendering API)
      enhanceQuantityDropdown();

      // Re-apply badges (HTML was replaced)
      applyBadges();

      // Re-find the price element (it was replaced) and update
      if (priceModel === 'per-unit') {
        priceEl = null;
        initPriceDisplay();
      }

      // Update max photos from the newly selected variant (match_variant only)
      if (maxMode === 'match_variant') {
        readCurrentPackSize();
      }
    });
  }

  // =========================================================================
  // Distribution preview calculator
  // =========================================================================

  /**
   * Update the distribution preview text.
   * Shows how photos will be distributed across the pack.
   * Skipped in exact mode (each photo = 1 magnet, no repetition).
   */
  function updateDistribution() {
    if (!distributionEl || !showDistribution) return;

    // In exact mode, distribution is not applicable (1:1 mapping)
    if (uploadMode === 'exact') {
      distributionEl.style.display = 'none';
      return;
    }

    var photoCount = uploadedPhotos.length;
    if (photoCount === 0) {
      distributionEl.style.display = 'none';
      return;
    }

    distributionEl.style.display = '';
    var packSize = maxPhotos;

    // e.g. "Your 2 photos → 35 magnets (each photo prints 17–18 times)"
    var photoLabel = photoCount + ' photo' + (photoCount > 1 ? 's' : '');

    if (photoCount >= packSize) {
      distributionEl.textContent = 'Your ' + photoLabel + ' \u2192 ' +
        packSize + ' magnets (1 print each)';
    } else {
      var base = Math.floor(packSize / photoCount);
      var extra = packSize % photoCount;
      var printsPart;
      if (extra === 0) {
        printsPart = base + ' print' + (base > 1 ? 's' : '') + ' each';
      } else {
        printsPart = base + '\u2013' + (base + 1) + ' prints each';
      }
      distributionEl.textContent = 'Your ' + photoLabel + ' \u2192 ' +
        packSize + ' magnets (' + printsPart + ')';
    }
  }

  // =========================================================================
  // Quantity dropdown enhancement (per-unit pricing)
  // Supports both pricing models:
  //   - per-unit: variant.price is per-unit, calculate total (fridge magnets)
  //   - total: variant.price is total, calculate per-unit (save-the-date)
  // =========================================================================

  /**
   * Load and cache product variant data from the page.
   * Reads from the [data-product-variants] JSON block output by main-product.liquid
   * when max_mode is match_variant.
   */
  function loadProductData() {
    if (productData) return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    var jsonEl = productInfo.querySelector('[data-product-variants]');
    if (!jsonEl) return;

    try {
      var variants = JSON.parse(jsonEl.textContent);
      productData = { variants: variants };
    } catch (e) {
      console.warn('PhotoUpload: Could not parse product variants JSON', e);
    }
  }

  /**
   * Enrich the quantity dropdown with pricing info.
   * Adapts to the pricing model:
   *   - per-unit model (fridge magnets): shows "20 — $3.49/ea"
   *   - total model (save-the-date): shows "50 — $1.28/ea"
   *
   * For multi-option products (e.g. save-the-date with Size + Quantity),
   * filters variants by the currently selected non-quantity options to show
   * correct prices for the active size.
   */
  function enhanceQuantityDropdown() {
    if (!productData || !productData.variants) return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    var variantSelectsEl = productInfo.querySelector('variant-selects');
    if (!variantSelectsEl) return;

    // Find the quantity dropdown (at the correct option index)
    var allOptionGroups = variantSelectsEl.querySelectorAll(
      '.product-form__input--pill, .product-form__input--swatch, .product-form__input--dropdown'
    );
    var targetIndex = qtyOptionIndex - 1;
    if (allOptionGroups.length <= targetIndex) return;

    var targetGroup = allOptionGroups[targetIndex];
    var select = targetGroup.querySelector('select');
    if (!select) return;

    // For multi-option products, get the currently selected value of the OTHER option
    // to filter variants correctly (e.g. show prices for "Medium" size only)
    var selectedOtherOptionValue = null;
    if (qtyOptionIndex === 2 && allOptionGroups.length > 0) {
      // Other option is at index 0
      var otherGroup = allOptionGroups[0];
      var otherSelect = otherGroup.querySelector('select');
      var otherRadio = otherGroup.querySelector('input[type="radio"]:checked');
      if (otherSelect) {
        selectedOtherOptionValue = otherSelect.value;
      } else if (otherRadio) {
        selectedOtherOptionValue = otherRadio.value;
      }
    } else if (qtyOptionIndex === 1 && allOptionGroups.length > 1) {
      var otherGroup = allOptionGroups[1];
      var otherSelect = otherGroup.querySelector('select');
      var otherRadio = otherGroup.querySelector('input[type="radio"]:checked');
      if (otherSelect) {
        selectedOtherOptionValue = otherSelect.value;
      } else if (otherRadio) {
        selectedOtherOptionValue = otherRadio.value;
      }
    }

    // Determine which option key holds the quantity (option1 or option2)
    var qtyOptionKey = 'option' + qtyOptionIndex;
    var otherOptionKey = qtyOptionIndex === 1 ? 'option2' : 'option1';

    productData.variants.forEach(function (variant) {
      // Filter by the other option if applicable
      if (selectedOtherOptionValue && variant[otherOptionKey] !== selectedOtherOptionValue) {
        return;
      }

      var qty = parseInt(variant[qtyOptionKey], 10);
      if (isNaN(qty) || qty <= 0) return;

      var unitPrice, totalPrice;
      if (priceModel === 'total') {
        // variant.price is total in cents — derive per-unit
        totalPrice = (variant.price / 100).toFixed(2);
        unitPrice = (variant.price / 100 / qty).toFixed(2);
      } else {
        // variant.price is per-unit in cents — derive total
        unitPrice = (variant.price / 100).toFixed(2);
        totalPrice = (variant.price / 100 * qty).toFixed(2);
      }

      // Match dropdown option by the quantity value
      var option = select.querySelector('option[value="' + variant[qtyOptionKey] + '"]');
      if (option) {
        // Dropdown should show per-unit only (total is shown elsewhere in the PDP).
        var sym = (window.shopCurrency && window.shopCurrency.symbol) || '$';
        var text = qty + ' \u2014 ' + sym + unitPrice + '/ea';
        // Append dropdown badge label if configured (e.g. "Most Popular")
        var config = PRODUCT_CONFIG[productHandle];
        if (config && config.dropdownBadges && config.dropdownBadges[String(qty)]) {
          text += '  \u2605 ' + config.dropdownBadges[String(qty)];
        }
        option.textContent = text;
      }
    });
  }

  // =========================================================================
  // Total price display (per-unit pricing model only)
  // Shopify variant prices are per-unit; we show total = unit x pack size
  // Skipped when priceModel === 'total' (Shopify already shows the total)
  // =========================================================================

  /**
   * Initialize the total price display.
   * Finds the Shopify price element and runs the first update.
   * Only runs for per-unit pricing model (e.g. fridge magnets).
   */
  function initPriceDisplay() {
    if (priceModel !== 'per-unit') return;
    if (!productData || !productData.variants) return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    priceEl = productInfo.querySelector('.price-item--regular');
    if (!priceEl) return;

    updatePriceDisplay();
  }

  /**
   * Update the main price to show total (unit price x pack size).
   * Only runs for per-unit pricing model.
   */
  function updatePriceDisplay() {
    if (priceModel !== 'per-unit') return;
    if (!priceEl || !productData || !productData.variants) return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    // Find the quantity option value
    var allOptionGroups = productInfo.querySelectorAll(
      'variant-selects .product-form__input--pill, variant-selects .product-form__input--swatch, variant-selects .product-form__input--dropdown'
    );
    var targetIndex = qtyOptionIndex - 1;
    if (allOptionGroups.length <= targetIndex) return;

    var targetGroup = allOptionGroups[targetIndex];
    var select = targetGroup.querySelector('select');
    if (!select) return;

    var selectedValue = select.value;
    var packSize = parseInt(selectedValue, 10);
    if (isNaN(packSize) || packSize <= 0) return;

    var qtyOptionKey = 'option' + qtyOptionIndex;
    var variant = productData.variants.find(function (v) {
      return v[qtyOptionKey] === selectedValue;
    });
    if (!variant) return;

    // variant.price is per-unit in cents
    var totalPrice = (variant.price / 100 * packSize).toFixed(2);
    priceEl.textContent = formatMoney(totalPrice);
  }

  // =========================================================================
  // AddToCartGuard — centralized validation for the submit button
  // =========================================================================

  /**
   * Update the submit button state based on upload validation.
   * - exact mode: disabled until uploads === maxPhotos
   * - flexible mode: never disabled by upload count (soft enforcement)
   */
  function updateSubmitButton() {
    if (uploadMode !== 'exact') return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    var submitBtn = productInfo.querySelector('[type="submit"]');
    if (!submitBtn) return;

    var photosValid = uploadedPhotos.length === maxPhotos;

    // Also check Save the Date event fields (if present on this PDP)
    var fieldsValid = true;
    if (window.SaveTheDateFields && typeof window.SaveTheDateFields.isValid === 'function') {
      fieldsValid = window.SaveTheDateFields.isValid();
    }

    var isValid = photosValid && fieldsValid;

    if (isValid) {
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.remove('photo-upload--blocked');
      if (errorEl) errorEl.setAttribute('hidden', '');
    } else {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('photo-upload--blocked');
      // Only show photo error if user has started uploading and photos are the issue
      if (errorEl && uploadedPhotos.length > 0 && !photosValid) {
        errorEl.removeAttribute('hidden');
      } else if (errorEl && photosValid) {
        // Photos are fine but fields aren't — hide the photo error
        errorEl.setAttribute('hidden', '');
      }
    }
  }

  // =========================================================================
  // Badge application — "Most Popular", "Best Value" on variant pills
  // =========================================================================

  /**
   * Apply badges to variant pill labels based on PRODUCT_CONFIG.
   * Runs after init and after each variantChange (since Shopify replaces HTML).
   */
  function applyBadges() {
    var config = PRODUCT_CONFIG[productHandle];
    if (!config) return;

    var productInfo = container.closest('product-info');
    if (!productInfo) return;

    var variantSelectsEl = productInfo.querySelector('variant-selects');
    if (!variantSelectsEl) return;

    // Apply badges to pill labels (size, pack size, etc.)
    if (config.badges) {
      applyBadgesToLabels(variantSelectsEl, config.badges);
    }
  }

  /**
   * Find pill labels matching the badge map keys and append badge spans.
   * @param {Element} variantSelectsEl - The variant-selects container
   * @param {Object} badgeMap - e.g. { 'Medium': 'Most Popular', '48': 'Best Value' }
   */
  function applyBadgesToLabels(variantSelectsEl, badgeMap) {
    // Find all pill radio inputs
    var radios = variantSelectsEl.querySelectorAll(
      '.product-form__input--pill input[type="radio"]'
    );

    radios.forEach(function (radio) {
      var label = variantSelectsEl.querySelector('label[for="' + radio.id + '"]');
      if (!label) return;

      // Skip if badge already applied
      if (label.querySelector('.variant-badge')) return;

      var value = radio.value;
      if (badgeMap[value]) {
        var badge = document.createElement('span');
        badge.className = 'variant-badge';
        badge.textContent = '\u2605 ' + badgeMap[value];
        label.appendChild(badge);
      }
    });
  }

  // --- Initialize ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // --- Public API ---
  window.PhotoUpload = {
    getPhotos: function () { return uploadedPhotos.slice(); },
    getCount: function () { return uploadedPhotos.length; },
    getMax: function () { return maxPhotos; },
    getPackSize: function () { return maxPhotos; },
    getUploadMode: function () { return uploadMode; },
    isValid: function () {
      if (uploadMode === 'exact') return uploadedPhotos.length === maxPhotos;
      return uploadedPhotos.length >= 1;
    },
    clear: function () {
      uploadedPhotos = [];
      window.UploadedPhotos = uploadedPhotos;
      if (previewGrid) previewGrid.innerHTML = '';
      updateCounter();
      updateDistribution();
      updateSubmitButton();
    },
    updateMax: function (newMax) { updateMaxPhotos(newMax); },
    refreshSubmitButton: function () { updateSubmitButton(); },
  };
})();
